/**
 * The dashboard route tree.
 *
 * Public:  /login
 * Authed:  everything under <RequireAuth> + <Layout>
 *   - meta-admin only:  /meta/orgs
 *   - org area (org-admin, or meta-admin with an active scope):
 *       /org/tickets, /org/tickets/:ticketId,
 *       /org/instances, /org/instances/:instanceId,
 *       /org/clusters
 *
 * Role gating uses <RequireRole>; org-scoped pages additionally use
 * <RequireOrgScope> so a meta-admin without a selected org is sent to pick one.
 */
import type { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext.js';
import { RequireAuth, RequireRole, homePathForRole } from '../auth/guards.js';
import { RequireOrgScope } from '../org/RequireOrgScope.js';
import { Layout } from '../components/Layout.js';

import LoginPage from './LoginPage.js';
import MagicLinkCallback from './MagicLinkCallback.js';
import OnboardPage from './OnboardPage.js';
import OrgsPage from './meta/OrgsPage.js';
import OrgEditorPage from './meta/OrgEditorPage.js';
import TicketFeedPage from './org/TicketFeedPage.js';
import TicketDetailPage from './org/TicketDetailPage.js';
import WidgetInstancesPage from './org/WidgetInstancesPage.js';
import ConfigEditorPage from './org/ConfigEditorPage.js';
import ClustersPage from './org/ClustersPage.js';

/** Redirect "/" to the signed-in user's home, or to /login. */
function RootRedirect(): ReactElement {
  const { status, principal } = useAuth();
  if (status === 'loading') {
    return <div className="route-loading" role="status">Loading…</div>;
  }
  if (status === 'authenticated' && principal) {
    return <Navigate to={homePathForRole(principal.role)} replace />;
  }
  return <Navigate to="/login" replace />;
}

/** Wrap an org-scoped element with the scope gate. */
function OrgScoped({ children }: { children: ReactElement }): ReactElement {
  return <RequireOrgScope>{children}</RequireOrgScope>;
}

export function AppRoutes(): ReactElement {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/magic" element={<MagicLinkCallback />} />
      {/* Public: prefill onboarding target for the hotline-widget skill deep-link. */}
      <Route path="/onboard" element={<OnboardPage />} />

      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        {/* Meta-admin */}
        <Route
          path="/meta/orgs"
          element={
            <RequireRole roles={['META_ADMIN']}>
              <OrgsPage />
            </RequireRole>
          }
        />
        <Route
          path="/meta/orgs/:orgId"
          element={
            <RequireRole roles={['META_ADMIN']}>
              <OrgEditorPage />
            </RequireRole>
          }
        />

        {/* Org area */}
        <Route
          path="/org/tickets"
          element={
            <OrgScoped>
              <TicketFeedPage />
            </OrgScoped>
          }
        />
        <Route
          path="/org/tickets/:ticketId"
          element={
            <OrgScoped>
              <TicketDetailPage />
            </OrgScoped>
          }
        />
        <Route
          path="/org/instances"
          element={
            <OrgScoped>
              <WidgetInstancesPage />
            </OrgScoped>
          }
        />
        <Route
          path="/org/instances/:instanceId"
          element={
            <OrgScoped>
              <ConfigEditorPage />
            </OrgScoped>
          }
        />
        <Route
          path="/org/clusters"
          element={
            <OrgScoped>
              <ClustersPage />
            </OrgScoped>
          }
        />
      </Route>

      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
